import React, { useState } from 'react';
import { motion } from 'framer-motion';
import type { Product } from '@/data/labsProducts';

const tabs = [
  { key: 'build', label: 'What We Build' },
  { key: 'why', label: 'Why Us, Why Now' },
  { key: 'competition', label: 'Competitive Landscape' },
  { key: 'revenue', label: 'Revenue Potential' },
] as const;

type TabKey = (typeof tabs)[number]['key'];

const ProductCard: React.FC<{ product: Product; index: number }> = ({ product, index }) => {
  const [activeTab, setActiveTab] = useState<TabKey>('build');

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ delay: 0.1 }}
      className="group relative rounded-xl border border-[#1F1F2E] bg-[#0F0F18] overflow-hidden hover:border-[#3B82F6]/30 transition-colors duration-500"
    >
      {/* Glow effect on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#3B82F6]/[0.02] to-[#8B5CF6]/[0.02] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative p-6 sm:p-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-[#3B82F6] uppercase tracking-widest font-medium">
                Product {String(product.id).padStart(2, '0')}
              </span>
              {product.status === 'live' && (
                <span className="text-[9px] px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 uppercase tracking-wider font-semibold">
                  Live — {product.launchDate}
                </span>
              )}
            </div>
            <h3 className="font-space-grotesk text-xl sm:text-2xl font-bold text-white mt-1 leading-tight">
              {product.icon} {product.name}
            </h3>
            {product.traction && (
              <p className="text-emerald-400 text-xs mt-1 font-medium">
                📈 {product.traction}
              </p>
            )}
          </div>
          {product.status !== 'live' && (
            <a
              href="#footer-cta"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('footer-cta')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="hidden sm:inline-flex text-[10px] px-3 py-1.5 rounded-full border border-[#3B82F6]/30 text-[#3B82F6] hover:bg-[#3B82F6]/10 transition-colors whitespace-nowrap"
            >
              Apply to Build This
            </a>
          )}
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 mb-6 border-b border-[#1F1F2E] pb-px">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`text-[11px] px-3 py-2 transition-colors relative ${
                activeTab === tab.key
                  ? 'text-white'
                  : 'text-[#A1A1AA]/50 hover:text-[#A1A1AA]'
              }`}
            >
              {tab.label}
              {activeTab === tab.key && (
                <motion.div
                  layoutId={`tab-underline-${product.id}`}
                  className="absolute bottom-0 left-0 right-0 h-px bg-[#3B82F6]"
                />
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="min-h-[200px]">
          {activeTab === 'build' && (
            <p className="text-[#A1A1AA] text-sm leading-relaxed">{product.whatWeBuild}</p>
          )}

          {activeTab === 'why' && (
            <p className="text-[#A1A1AA] text-sm leading-relaxed">{product.whyUsWhyNow}</p>
          )}

          {activeTab === 'competition' && (
            <div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-[#1F1F2E]">
                      <th className="text-left py-2 pr-4 text-[#A1A1AA]/50 font-medium">Company</th>
                      <th className="text-left py-2 pr-4 text-[#A1A1AA]/50 font-medium">HQ</th>
                      <th className="text-left py-2 pr-4 text-[#A1A1AA]/50 font-medium">Raised</th>
                      <th className="text-left py-2 pr-4 text-[#A1A1AA]/50 font-medium">Approach</th>
                      <th className="text-left py-2 text-[#A1A1AA]/50 font-medium">Mexico?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {product.competitors.map((c, i) => (
                      <tr
                        key={c.company}
                        className={`border-b border-[#1F1F2E]/50 ${i % 2 === 1 ? 'bg-[#1F1F2E]/10' : ''}`}
                      >
                        <td className="py-2 pr-4 text-white font-medium">{c.company}</td>
                        <td className="py-2 pr-4 text-[#A1A1AA]">{c.hq}</td>
                        <td className="py-2 pr-4 text-[#A1A1AA]">{c.raised}</td>
                        <td className="py-2 pr-4 text-[#A1A1AA]">{c.approach}</td>
                        <td className="py-2 text-[#A1A1AA]">{c.mexico}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 p-3 rounded-lg bg-[#3B82F6]/5 border border-[#3B82F6]/10">
                <p className="text-[11px] text-[#3B82F6]">
                  <span className="font-semibold">Key insight:</span> {product.competitorInsight}
                </p>
              </div>
            </div>
          )}

          {activeTab === 'revenue' && (
            <ul className="space-y-3">
              {product.revenuePoints.map((point, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <span className="w-1 h-1 rounded-full bg-[#8B5CF6] mt-2 shrink-0" />
                  <span className="text-[#A1A1AA]">{point}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Mobile apply link */}
        {product.status !== 'live' && (
          <div className="mt-6 sm:hidden">
            <a
              href="#footer-cta"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('footer-cta')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-[11px] text-[#3B82F6] hover:text-[#60A5FA] transition-colors"
            >
              Apply to Build This →
            </a>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ProductCard;
